import { useMemo, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { documentService, type LibraryDocument } from '@/services/api/documentService';
import { QUERY_KEYS } from '@/services/queryClient';
import { useAppSelector } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { isAdminOrAbove } from '@/shared/utils/roles';
import { useSnackbar } from '@/shared/contexts/SnackbarContext';
import {
  ALL_FOLDERS_ID,
  errorMessage,
  fileKindKey,
  getRecentDocumentIds,
  isFolderSubtreeEmpty,
  pushRecentDocument,
  type DocScopeFilter,
  type DocSortDir,
  type DocSortKey,
  type FileTypeFilter,
} from '../documentUi';
import type { AddDocumentKind, PendingDelete } from '../documentsTypes';

export function useDocumentsLibrary() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useSnackbar();
  const user = useAppSelector(selectCurrentUser);
  const canManageCategories = isAdminOrAbove(user?.role);

  const [selectedCategoryId, setSelectedCategoryId] = useState(ALL_FOLDERS_ID);
  const [categoryName, setCategoryName] = useState('');
  const [categoryParentId, setCategoryParentId] = useState<string | null>(null);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [renameFolderOpen, setRenameFolderOpen] = useState(false);
  const [renameFolderName, setRenameFolderName] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [addKind, setAddKind] = useState<AddDocumentKind>('upload');
  const [addName, setAddName] = useState('');
  const [addUrl, setAddUrl] = useState('');
  const [addFile, setAddFile] = useState<File | null>(null);
  const [formError, setFormError] = useState('');
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<FileTypeFilter>('all');
  const [scopeFilter, setScopeFilter] = useState<DocScopeFilter>('all');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = useState<DocSortKey>('createdAt');
  const [sortDir, setSortDir] = useState<DocSortDir>('desc');
  const [previewDoc, setPreviewDoc] = useState<LibraryDocument | null>(null);
  const [activityOpen, setActivityOpen] = useState(false);
  const [shareFolderOpen, setShareFolderOpen] = useState(false);
  const [moveDocIds, setMoveDocIds] = useState<string[]>([]);
  const [moveTargetId, setMoveTargetId] = useState('');
  const [recentTick, setRecentTick] = useState(0);

  const statusQuery = useQuery({
    queryKey: QUERY_KEYS.documentStatus,
    queryFn: () => documentService.getStatus(),
  });

  const categoriesQuery = useQuery({
    queryKey: QUERY_KEYS.documentCategories(canManageCategories),
    queryFn: () => documentService.listCategories(canManageCategories),
  });

  const categories = useMemo(
    () => (categoriesQuery.data || []).filter((c) => c.isActive || canManageCategories),
    [categoriesQuery.data, canManageCategories]
  );
  const activeFolders = useMemo(() => categories.filter((c) => c.isActive), [categories]);
  const parentCategory = categoryParentId ? categories.find((c) => c.id === categoryParentId) || null : null;
  const selectedFolder = categories.find((c) => c.id === selectedCategoryId) || null;
  const activeCategoryId =
    selectedCategoryId === ALL_FOLDERS_ID || categories.some((c) => c.id === selectedCategoryId)
      ? selectedCategoryId
      : ALL_FOLDERS_ID;
  const addTargetFolderId = selectedFolder?.id ?? null;
  const canDeleteSelectedFolder = Boolean(
    selectedFolder && isFolderSubtreeEmpty(categories, selectedFolder.id)
  );
  const canUploadToSelected =
    Boolean(selectedFolder)
    && !selectedFolder?.pathOnly
    && (
      canManageCategories
      || selectedFolder?.myRole === 'viewer'
      || selectedFolder?.myRole === 'editor'
    );

  const openAdd = (kind: AddDocumentKind = 'upload') => {
    if (!canUploadToSelected) return;
    setFormError('');
    setAddKind(kind);
    setAddOpen(true);
  };

  const documentsQuery = useQuery({
    queryKey: QUERY_KEYS.documents(activeCategoryId),
    queryFn: async () => {
      if (activeCategoryId === ALL_FOLDERS_ID) {
        const lists = await Promise.all(
          categories
            .filter((c) => !c.pathOnly)
            .map((c) => documentService.listDocuments(c.id))
        );
        return lists.flat();
      }
      return documentService.listDocuments(activeCategoryId);
    },
    enabled:
      (activeCategoryId === ALL_FOLDERS_ID
        ? categories.some((c) => !c.pathOnly)
        : Boolean(activeCategoryId) && !selectedFolder?.pathOnly),
  });

  const invalidateLibrary = () => {
    queryClient.invalidateQueries({ queryKey: ['documents', 'categories'] });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.documents(activeCategoryId) });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.documents(ALL_FOLDERS_ID) });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.documentActivity });
  };

  const openCategoryDialog = (parentId: string | null) => {
    setFormError('');
    setCategoryName('');
    setCategoryParentId(parentId);
    setCategoryDialogOpen(true);
  };

  const createCategory = useMutation({
    mutationFn: () => documentService.createCategory(categoryName, categoryParentId),
    onSuccess: (created) => {
      setCategoryDialogOpen(false);
      setCategoryName('');
      setCategoryParentId(null);
      setFormError('');
      setSelectedCategoryId(created.id);
      queryClient.invalidateQueries({ queryKey: ['documents', 'categories'] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.documentActivity });
    },
    onError: (err) => setFormError(errorMessage(err)),
  });

  const renameCategory = useMutation({
    mutationFn: () => documentService.updateCategory(selectedFolder!.id, { name: renameFolderName }),
    onSuccess: () => {
      setRenameFolderOpen(false);
      invalidateLibrary();
      showSuccess('Folder renamed');
    },
    onError: (err) => showError(errorMessage(err)),
  });

  const deactivateCategory = useMutation({
    mutationFn: (id: string) => documentService.deactivateCategory(id),
    onSuccess: () => {
      setSelectedCategoryId(ALL_FOLDERS_ID);
      invalidateLibrary();
      showSuccess('Folder hidden');
    },
    onError: (err) => showError(errorMessage(err)),
  });

  const deleteEmptyCategory = useMutation({
    mutationFn: (id: string) => documentService.deleteEmptyCategory(id),
    onSuccess: () => {
      setSelectedCategoryId(ALL_FOLDERS_ID);
      invalidateLibrary();
      showSuccess('Folder deleted');
    },
    onError: (err) => showError(errorMessage(err)),
  });

  const restoreCategory = useMutation({
    mutationFn: (id: string) => documentService.updateCategory(id, { isActive: true }),
    onSuccess: () => {
      invalidateLibrary();
      showSuccess('Folder visible again');
    },
    onError: (err) => showError(errorMessage(err)),
  });

  const addDocument = useMutation({
    mutationFn: async () => {
      if (!addTargetFolderId) throw new Error('Select a folder first');
      if (addKind === 'upload') {
        if (!addFile) throw new Error('Choose a file to upload');
        return documentService.uploadFile(addTargetFolderId, addFile, addName || addFile.name);
      }
      if (addKind === 'google_doc' || addKind === 'google_sheet') {
        return documentService.createGoogleFile(addTargetFolderId, addKind, addName);
      }
      return documentService.addDriveLink(addTargetFolderId, addName, addUrl);
    },
    onSuccess: () => {
      setAddOpen(false);
      setAddName('');
      setAddUrl('');
      setAddFile(null);
      setFormError('');
      invalidateLibrary();
    },
    onError: (err) => setFormError(errorMessage(err)),
  });

  const deleteDocument = useMutation({
    mutationFn: (id: string) => documentService.deleteDocument(id),
    onSuccess: () => {
      setSelectedIds(new Set());
      invalidateLibrary();
    },
    onError: (err) => showError(errorMessage(err)),
  });

  const bulkDelete = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map((id) => documentService.deleteDocument(id)));
    },
    onSuccess: () => {
      setSelectedIds(new Set());
      invalidateLibrary();
      showSuccess('Documents deleted');
    },
    onError: (err) => showError(errorMessage(err)),
  });

  const moveDocuments = useMutation({
    mutationFn: async ({ ids, categoryId }: { ids: string[]; categoryId: string }) => {
      await Promise.all(ids.map((id) => documentService.moveDocument(id, categoryId)));
    },
    onSuccess: () => {
      setMoveDocIds([]);
      setMoveTargetId('');
      setSelectedIds(new Set());
      invalidateLibrary();
      showSuccess('Moved to folder');
    },
    onError: (err) => showError(errorMessage(err)),
  });

  const toggleFavorite = useMutation({
    mutationFn: (doc: LibraryDocument) => documentService.setFavorite(doc.id, !doc.isFavorite),
    onSuccess: (_, doc) => {
      invalidateLibrary();
      showSuccess(doc.isFavorite ? 'Removed from favorites' : 'Added to favorites');
    },
    onError: (err) => showError(errorMessage(err)),
  });

  const documents = documentsQuery.data || [];
  const totalFiles = categories.reduce((sum, c) => sum + (c.documentCount || 0), 0);
  const storageBytes = documents.reduce((sum, d) => sum + (d.size || 0), 0);
  const recentIds = useMemo(() => getRecentDocumentIds(), [recentTick, documents]);

  const filteredFiles = useMemo(() => {
    const recentSet = new Set(recentIds);
    return documents.filter((doc) => {
      const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = typeFilter === 'all' || fileKindKey(doc) === typeFilter;
      const matchesScope =
        scopeFilter === 'all'
        || (scopeFilter === 'favorites' && doc.isFavorite)
        || (scopeFilter === 'recent' && recentSet.has(doc.id));
      return matchesSearch && matchesType && matchesScope;
    });
  }, [documents, searchTerm, typeFilter, scopeFilter, recentIds]);

  const copyLink = async (doc: LibraryDocument) => {
    try {
      await navigator.clipboard.writeText(doc.webViewUrl);
      showSuccess('Link copied');
    } catch {
      showError('Could not copy link');
    }
  };

  const copyLinks = async (ids: string[]) => {
    const urls = documents.filter((d) => ids.includes(d.id)).map((d) => d.webViewUrl).join('\n');
    try {
      await navigator.clipboard.writeText(urls);
      showSuccess(`Copied ${ids.length} link${ids.length === 1 ? '' : 's'}`);
    } catch {
      showError('Could not copy links');
    }
  };

  const openPreview = (doc: LibraryDocument) => {
    pushRecentDocument(doc.id);
    setRecentTick((n) => n + 1);
    setPreviewDoc(doc);
  };

  const openExternal = (doc: LibraryDocument) => {
    pushRecentDocument(doc.id);
    setRecentTick((n) => n + 1);
    window.open(doc.webViewUrl, '_blank');
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (ids: string[]) => {
    setSelectedIds((prev) => {
      const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const handleSortChange = (key: DocSortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'name' || key === 'createdByName' ? 'asc' : 'desc');
    }
  };

  const selectFolder = (id: string) => {
    setSelectedCategoryId(id);
    setSelectedIds(new Set());
    setMobileSidebarOpen(false);
  };

  const openRenameFolder = () => {
    if (!selectedFolder) return;
    setRenameFolderName(selectedFolder.name);
    setRenameFolderOpen(true);
  };

  const closeMoveDialog = () => {
    setMoveDocIds([]);
    setMoveTargetId('');
  };

  return {
    isMobile,
    user,
    canManageCategories,
    categories,
    activeFolders,
    parentCategory,
    selectedFolder,
    activeCategoryId,
    addTargetFolderId,
    canDeleteSelectedFolder,
    canUploadToSelected,
    categoriesLoading: categoriesQuery.isLoading,
    documentsLoading: documentsQuery.isLoading,
    documentsError: documentsQuery.isError ? errorMessage(documentsQuery.error) : null,
    configured: statusQuery.data?.configured ?? true,
    driveReady: statusQuery.data?.ready ?? true,
    driveMessage: statusQuery.data?.message,
    totalFiles,
    storageBytes,
    filteredFiles,
    selectedIds,
    sortKey,
    sortDir,
    searchTerm,
    setSearchTerm,
    typeFilter,
    setTypeFilter,
    scopeFilter,
    setScopeFilter,
    viewMode,
    setViewMode,
    visibleViewMode: (isMobile ? 'grid' : viewMode) as 'list' | 'grid',
    mobileSidebarOpen,
    setMobileSidebarOpen,
    previewDoc,
    setPreviewDoc,
    activityOpen,
    setActivityOpen,
    shareFolderOpen,
    setShareFolderOpen,
    categoryDialogOpen,
    setCategoryDialogOpen,
    categoryName,
    setCategoryName,
    renameFolderOpen,
    setRenameFolderOpen,
    renameFolderName,
    setRenameFolderName,
    addOpen,
    setAddOpen,
    addKind,
    setAddKind,
    addName,
    setAddName,
    addUrl,
    setAddUrl,
    addFile,
    setAddFile,
    formError,
    pendingDelete,
    setPendingDelete,
    moveDocIds,
    moveTargetId,
    setMoveTargetId,
    createCategory,
    renameCategory,
    deactivateCategory,
    deleteEmptyCategory,
    restoreCategory,
    addDocument,
    deleteDocument,
    bulkDelete,
    moveDocuments,
    toggleFavorite,
    openCategoryDialog,
    openAdd,
    openRenameFolder,
    openPreview,
    openExternal,
    copyLink,
    copyLinks,
    toggleSelect,
    toggleSelectAll,
    handleSortChange,
    selectFolder,
    closeMoveDialog,
    startMove: (ids: string[], targetId: string) => {
      setMoveDocIds(ids);
      setMoveTargetId(targetId);
    },
  };
}

export type DocumentsLibraryState = ReturnType<typeof useDocumentsLibrary>;
